/**
 * Splits, sectors and comparisons for one track's runs, all in simulation
 * seconds measured from the attempt start (AttemptManager / TimerSystem).
 *
 * A sector is the time between two consecutive timing landmarks of a run: the
 * start, each checkpoint in the order it was reached, and the finish. Sectors
 * are identified by their two landmarks ("start>0", "0>1", "1>finish"), so
 * runs that take different routes (and therefore reach different checkpoints,
 * or reach them in another order) are only ever compared on sectors they
 * actually share. There is no continuous "time difference" from spatial
 * proximity: comparisons happen only at landmarks both runs crossed.
 */

/** A checkpoint crossing: the checkpoint's index and the run time when it was entered (sub-tick precise). */
export interface Split {
  index: number;
  time: number;
}

export type Landmark = number | 'start' | 'finish';

export interface Sector {
  /** Stable identifier, e.g. "start>0", "0>1", "2>finish". */
  key: string;
  from: Landmark;
  to: Landmark;
  /** Seconds spent in the sector. */
  time: number;
  /** Run time at the end of the sector. */
  end: number;
}

const name = (l: Landmark): string => String(l);
export const sectorKey = (from: Landmark, to: Landmark): string => `${name(from)}>${name(to)}`;

/** Human label: "Start → CP 1", "CP 2 → Finish". Checkpoint indices are shown 1-based. */
export function sectorLabel(s: Pick<Sector, 'from' | 'to'>): string {
  const l = (x: Landmark): string => (x === 'start' ? 'Start' : x === 'finish' ? 'Finish' : `CP ${x + 1}`);
  return `${l(s.from)} → ${l(s.to)}`;
}

/** Sectors of a run. Without a finish time the last sector (to the finish) is omitted. */
export function sectors(splits: readonly Split[], finish: number | null): Sector[] {
  const out: Sector[] = [];
  let prev: Landmark = 'start';
  let prevTime = 0;
  for (const s of splits) {
    out.push({ key: sectorKey(prev, s.index), from: prev, to: s.index, time: s.time - prevTime, end: s.time });
    prev = s.index;
    prevTime = s.time;
  }
  if (finish !== null) out.push({ key: sectorKey(prev, 'finish'), from: prev, to: 'finish', time: finish - prevTime, end: finish });
  return out;
}

export interface SplitDelta {
  index: number;
  time: number;
  reference: number;
  /** Negative = ahead of the reference. */
  delta: number;
}

/**
 * Live comparison at a checkpoint: the reference run's time at the same
 * checkpoint, if the reference reached it after the same sequence of earlier
 * checkpoints. Otherwise null (different route: not comparable here).
 */
export function compareAt(current: readonly Split[], reference: readonly Split[]): SplitDelta | null {
  const n = current.length;
  if (n === 0 || reference.length < n) return null;
  for (let i = 0; i < n; i++) if (current[i]!.index !== reference[i]!.index) return null;
  const c = current[n - 1]!;
  const r = reference[n - 1]!;
  return { index: c.index, time: c.time, reference: r.time, delta: c.time - r.time };
}

export interface SectorComparison {
  key: string;
  label: string;
  a: number | null;
  b: number | null;
  /** a − b when both runs have the sector. */
  delta: number | null;
}

/**
 * Sector-by-sector comparison of two runs, in run A's order, followed by
 * sectors only run B has. A sector present in only one run is listed with
 * the other side null: it is not comparable (the runs went different ways).
 */
export function compareSectors(a: readonly Sector[], b: readonly Sector[]): SectorComparison[] {
  const bm = new Map(b.map((s) => [s.key, s]));
  const am = new Map(a.map((s) => [s.key, s]));
  const rows: SectorComparison[] = a.map((s) => {
    const o = bm.get(s.key);
    return { key: s.key, label: sectorLabel(s), a: s.time, b: o?.time ?? null, delta: o ? s.time - o.time : null };
  });
  for (const s of b) if (!am.has(s.key)) rows.push({ key: s.key, label: sectorLabel(s), a: null, b: s.time, delta: null });
  return rows;
}

/** Merges a run's sectors into the per-sector bests (returns a new map). */
export function mergeBestSectors(best: ReadonlyMap<string, number>, run: readonly Sector[]): Map<string, number> {
  const out = new Map(best);
  for (const s of run) {
    if (!(Number.isFinite(s.time) && s.time > 0)) continue;
    const prev = out.get(s.key);
    if (prev === undefined || s.time < prev) out.set(s.key, s.time);
  }
  return out;
}

/** Reasons a finished or interrupted attempt does not count as a personal best. Empty = eligible. */
export type IneligibleReason = 'practice' | 'deaths' | 'interrupted' | 'modified-rules' | 'test-start' | 'imported';

export const INELIGIBLE_TEXT: Readonly<Record<IneligibleReason, string>> = {
  practice: 'Practice run: practice times are never recorded.',
  deaths: 'The run included a death.',
  interrupted: 'The run was interrupted (pause, stall or lost graphics), so its time is not trustworthy.',
  'modified-rules': 'Movement tuning or an experiment was active: the run does not use the shipped movement rules.',
  'test-start': 'The run started from an editor test position, not the track start.',
  imported: 'Imported recordings are for viewing only and never count as your records.',
};

export interface EligibilityInput {
  mode: 'practice' | 'full-run';
  deaths: number;
  interrupted: boolean;
  /** The simulation's record key equals the track's baseline key. */
  baselineRules: boolean;
  testStart?: boolean;
  imported?: boolean;
}

export function ineligibleReasons(e: EligibilityInput): IneligibleReason[] {
  const out: IneligibleReason[] = [];
  if (e.imported) out.push('imported');
  if (e.testStart) out.push('test-start');
  if (e.mode !== 'full-run') out.push('practice');
  if (e.deaths > 0) out.push('deaths');
  if (e.interrupted) out.push('interrupted');
  if (!e.baselineRules) out.push('modified-rules');
  return out;
}
